/**
 * El rol de quien opera esta pestaña, para la PANTALLA: «equipo» (Deenex), «comercio» (una persona de
 * un cliente, Accesos) o «pendiente» (la sesión de persona todavía no canjeó). Lo decide `api.ts` con el
 * rol que devuelve el canje (nunca con el email del token) y lo publica acá; las pantallas lo leen con
 * `useEsEquipoDeenex` y se enteran cuando cambia.
 *
 * Vive aparte de `api.ts` para que leerlo no dependa del módulo entero: `api.ts` se registra como la
 * fuente al cargarse (lo carga AuthContext, antes de cualquier pantalla). Sin fuente (un test que
 * reemplaza `api.ts`) es «equipo», lo de siempre. Decide sólo qué se MUESTRA: lo que una persona de un
 * cliente no puede hacer lo frena el backend igual (403 SOLO_EQUIPO).
 */

export type RolActual = "equipo" | "comercio" | "pendiente";

type Fuente = { rolActual: () => RolActual; asegurarRol: () => Promise<void> };

let fuente: Fuente | null = null;
const oyentes = new Set<() => void>();

/**
 * Qué pasó con el último intento de saber el rol, para la página que lo espera (`EsperandoRol`). Antes, si el canje
 * fallaba (la red, un 5xx, otra pestaña que cambió de comercio, ningún comercio asignado), `PaginaSoloEquipo` y
 * Configuración se quedaban en «Cargando…» para siempre: nadie volvía a pedirlo ni decía nada. `recargar`: lo que
 * pasó no se arregla reintentando (otra pestaña cambió de comercio), hay que recargar la página.
 */
export type IntentoRol = { intentando: boolean; error: { texto: string; recargar: boolean } | null };
const SIN_INTENTO: IntentoRol = { intentando: false, error: null };
let intento: IntentoRol = SIN_INTENTO;
let enCurso: Promise<void> | null = null;

export const TEXTO_ROL_SIN_RESPUESTA = "No pudimos confirmar tu acceso: el servidor no respondió.";

/** Lo llama `api.ts` al cargarse. Una fuente nueva arranca sin intentos (lo de la anterior no vale). */
export function registrarFuenteRol(f: Fuente): void {
  fuente = f;
  intento = SIN_INTENTO;
  enCurso = null;
}

/** Lo llama `api.ts` cada vez que el rol puede haber cambiado (un canje, entrar, salir). */
export function avisarCambioRol(): void {
  // Con el rol ya sabido, el error de un intento anterior no va más.
  if (intento.error && rolActual() !== "pendiente") intento = SIN_INTENTO;
  for (const f of oyentes) f();
}

/** Para `useSyncExternalStore`. */
export function alCambiarRol(f: () => void): () => void {
  oyentes.add(f);
  return () => { oyentes.delete(f); };
}

export function rolActual(): RolActual {
  return fuente ? fuente.rolActual() : "equipo";
}

export function esEquipoDeenex(): boolean {
  return rolActual() === "equipo";
}

/** Para `useSyncExternalStore` (el mismo objeto mientras no cambia). */
export function intentoRol(): IntentoRol {
  return intento;
}

function fijarIntento(nuevo: IntentoRol): void {
  intento = nuevo;
  for (const f of oyentes) f();
}

function errorDeRol(e: unknown): NonNullable<IntentoRol["error"]> {
  const x = (e && typeof e === "object" ? e : {}) as { message?: unknown; body?: { error?: unknown; code?: unknown } };
  const texto = (typeof x.body?.error === "string" && x.body.error.trim()) || (typeof x.message === "string" && x.message.trim()) || TEXTO_ROL_SIN_RESPUESTA;
  const code = x.body?.code;
  return { texto, recargar: code === "COMERCIO_CAMBIADO_EN_OTRA_PESTANA" || code === "COMERCIO_CAMBIADO" };
}

/**
 * Si el rol está pendiente, que `api.ts` canjee ya (ver `activaApi.asegurarRol`). Un intento a la vez: lo piden todas
 * las piezas de la pantalla que esperan el rol, y el «Reintentar». Si falla, queda el error en `intentoRol()`; no se
 * vuelve a intentar solo (sin loops): lo reintenta la persona, o la próxima pieza que se monte.
 */
export function asegurarRol(): Promise<void> {
  if (!fuente) return Promise.resolve();
  if (enCurso) return enCurso;
  const f = fuente;
  fijarIntento({ intentando: true, error: intento.error });
  enCurso = f.asegurarRol()
    .then(() => fijarIntento(SIN_INTENTO), (e) => fijarIntento({ intentando: false, error: errorDeRol(e) }))
    .finally(() => { enCurso = null; });
  return enCurso;
}
